
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const subscriptionCache = {
  hasActiveSubscription: false,
  checkedAt: 0,
  userId: null,
  CACHE_DURATION: 15 * 60 * 1000 
};

interface SubscriptionCheckProps {
  children: React.ReactNode;
}

export const SubscriptionCheck = ({ children }: SubscriptionCheckProps) => {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const MAX_RETRIES = 2;

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      if (loading) {
        setShowLoadingUI(true);
      }
    }, 300);

    const checkSubscription = async () => {
      try {
        console.log('Checking subscription status for user...');
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          console.log('No session found, redirecting to login');
          toast({
            title: "Authentication Required",
            description: "Please log in to access this content.",
            variant: "destructive",
          });
          navigate('/login');
          return;
        }

        console.log(`User authenticated: ${session.user.id}`);
        
        const currentUserId = session.user.id;
        const currentTime = Date.now();

        if (
          subscriptionCache.userId === currentUserId && 
          subscriptionCache.checkedAt > 0 && 
          currentTime - subscriptionCache.checkedAt < subscriptionCache.CACHE_DURATION
        ) {
          console.log('Using cached subscription status:', subscriptionCache.hasActiveSubscription);
          setHasSubscription(subscriptionCache.hasActiveSubscription);
          setLoading(false);
          return;
        }

        console.log('Checking subscription for user:', currentUserId);

        // Direct query to get ALL subscriptions for debugging
        const { data: allSubscriptions } = await supabase
          .from('subscriptions')
          .select('plan_type, status, created_at, stripe_subscription_id, stripe_customer_id, trial_ends_at')
          .eq('user_id', currentUserId);
          
        console.log('Latest subscription data from database:', allSubscriptions);

        // Set default plan from database if it exists
        if (allSubscriptions && allSubscriptions.length > 0) {
          const latestSub = allSubscriptions[0];
          console.log('Setting current plan from database to:', latestSub.plan_type);
        }

        const { data: subscription, error } = await supabase
          .from('subscriptions')
          .select('plan_type, status, is_test_mode, stripe_subscription_id, trial_ends_at')
          .eq('user_id', currentUserId)
          .maybeSingle();

        if (error) {
          console.error('Error checking subscription:', error);
          throw error;
        }

        console.log('Subscription data:', subscription);

        // Check if trial is still valid
        const hasValidTrial = subscription?.status === 'trialing' && 
                             subscription?.trial_ends_at && 
                             new Date(subscription.trial_ends_at) > new Date();
        
        console.log('Has valid trial:', hasValidTrial);
        if (hasValidTrial) {
          console.log('Trial is still valid, expiring at:', subscription.trial_ends_at);
        }

        // Allow access based on subscription
        if (subscription && (subscription.status === 'active' || 
                             subscription.status === 'trialing' || 
                             hasValidTrial ||
                             subscription.status === 'past_due' ||
                             (subscription.plan_type === 'lifetime' && subscription.status !== 'canceled'))) {
          console.log('Active subscription or valid trial found in database, allowing access');
          
          subscriptionCache.hasActiveSubscription = true;
          subscriptionCache.checkedAt = currentTime;
          subscriptionCache.userId = currentUserId;
          
          setHasSubscription(true);
          setLoading(false);
          return;
        }

        // Skip Stripe check if we've already retried too many times
        if (retryCount >= MAX_RETRIES) {
          // If a manual subscription was just created, grant access
          const manualSubscriptionPattern = /^manual_\d+$/;
          if (subscription && subscription.stripe_subscription_id && 
              manualSubscriptionPattern.test(subscription.stripe_subscription_id)) {
            console.log('Manual subscription found, allowing access');
            subscriptionCache.hasActiveSubscription = true;
            subscriptionCache.checkedAt = currentTime;
            subscriptionCache.userId = currentUserId;
            
            setHasSubscription(true);
            setLoading(false);
            return;
          }
          
          // Otherwise redirect to paywall
          console.log(`Maximum retries (${MAX_RETRIES}) reached, redirecting to paywall`);
          navigate('/paywall');
          return;
        }
        
        console.log('No active subscription found in database, checking with Stripe...');
        
        try {
          console.log('Calling verify-stripe-subscription function');
          const { data: stripeCheckResult, error: stripeCheckError } = await supabase.functions.invoke('verify-stripe-subscription', {
            body: { userId: currentUserId }
          });
          
          if (stripeCheckError) {
            console.error('Error checking Stripe subscription:', stripeCheckError);
            throw stripeCheckError;
          }
          
          console.log('Stripe verification result:', stripeCheckResult);
          
          if (stripeCheckResult && (stripeCheckResult.hasActiveSubscription || stripeCheckResult.message === "Recent payment found")) {
            console.log('Active subscription found in Stripe, allowing access');
            
            if (stripeCheckResult.subscription) {
              const planType = stripeCheckResult.subscription.metadata?.plan_type || 'monthly';
              const { error: updateError } = await supabase
                .from('subscriptions')
                .upsert({
                  user_id: currentUserId,
                  plan_type: planType,
                  status: 'active',
                  stripe_subscription_id: stripeCheckResult.subscription.id,
                  stripe_customer_id: stripeCheckResult.subscription.customer,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                });
                
              if (updateError) {
                console.error('Error updating subscription in database:', updateError);
              }
            } else if (stripeCheckResult.message === "Recent payment found") {
              console.log('Recent payment found, treating as active subscription');
              // Insert a temporary subscription entry
              const { error: insertError } = await supabase
                .from('subscriptions')
                .insert({
                  user_id: currentUserId,
                  plan_type: 'monthly', // Default to monthly if we don't know the plan
                  status: 'active',
                  stripe_subscription_id: 'pending_sync',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                });
                
              if (insertError) {
                console.error('Error inserting temporary subscription:', insertError);
              }
            }
            
            subscriptionCache.hasActiveSubscription = true;
            subscriptionCache.checkedAt = currentTime;
            subscriptionCache.userId = currentUserId;
            
            setHasSubscription(true);
            setLoading(false);
            return;
          }
          
          // No subscription found, retry if under max retries
          if (retryCount < MAX_RETRIES) {
            console.log(`No subscription found, retrying verification (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
            setRetryCount(prevCount => prevCount + 1);
            setTimeout(() => checkSubscription(), 3000);
            return;
          }
        } catch (stripeError) {
          console.error('Error in Stripe verification:', stripeError);
          
          if (retryCount >= MAX_RETRIES) {
            toast({
              title: "Error",
              description: "Failed to verify your subscription. Please try again later or contact support.",
              variant: "destructive",
            });
            navigate('/paywall');
          } else {
            setRetryCount(prevCount => prevCount + 1);
            setTimeout(() => checkSubscription(), 3000);
          }
          return;
        }
      } catch (error) {
        console.error('Error in subscription check:', error);
        
        if (retryCount >= MAX_RETRIES) {
          toast({
            title: "Error",
            description: "Failed to verify your subscription. Please try again later or contact support.",
            variant: "destructive",
          });
          navigate('/paywall');
        } else {
          setRetryCount(prevCount => prevCount + 1);
          setTimeout(() => checkSubscription(), 3000);
        }
      }
    };

    checkSubscription();

    return () => {
      clearTimeout(loadingTimer);
    };
  }, [navigate, toast, retryCount]);

  if (loading) {
    if (!showLoadingUI) {
      return null;
    }
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Verifying subscription</h2>
          <p className="text-gray-500">This will only take a moment...</p>
        </div>
      </div>
    );
  }

  return hasSubscription ? <>{children}</> : null;
};

export default SubscriptionCheck;
